import { useEffect, useState } from "react";
import { FileText, Folder } from "lucide-react";
import { getData, useItem, useWorkspaceItems } from "@/data/store";
import { openItem, createNote, createFolder } from "@/commands/nav";
import { Button } from "@/ui/Button";
import s from "./FolderView.module.css";

/**
 * Clicking a folder shows this: the folder title, its children as preview
 * cards, and (once set) its pinned folder note. The Trilium "every parent
 * is an index page" idea, hybrid-model flavored.
 */
export function FolderView({ folderId }: { folderId: string }) {
  const item = useItem(folderId);
  const items = useWorkspaceItems();
  const children = items.filter((i) => i.parentId === folderId && !i.trashedAt && !i.archivedAt);

  if (!item) return <div className={s.empty}>Folder not found</div>;

  return (
    <div className={s.view}>
      <div className={s.inner}>
        <input
          className={s.title}
          placeholder="Untitled folder"
          defaultValue={item.title}
          key={`${folderId}:${item.title === "" ? "empty" : "t"}`}
          onBlur={(e) => {
            if (e.target.value !== item.title) getData().workspace.rename(folderId, e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          }}
        />
        <div className={s.count}>
          {children.length === 0 ? "Empty folder" : `${children.length} item${children.length === 1 ? "" : "s"}`}
        </div>

        {children.length > 0 ? (
          <div className={s.grid}>
            {children.map((child) => (
              <ChildCard key={child.id} id={child.id} type={child.type} title={child.title} updatedAt={child.updatedAt} />
            ))}
          </div>
        ) : (
          <div className={s.empty}>Nothing in here yet.</div>
        )}

        <div className={s.newRow}>
          <Button variant="ghost" size="sm" onClick={() => createNote(folderId)}>
            New note
          </Button>
          <Button variant="subtle" size="sm" onClick={() => createFolder(folderId)}>
            New folder
          </Button>
        </div>
      </div>
    </div>
  );
}

function ChildCard({ id, type, title, updatedAt }: { id: string; type: string; title: string; updatedAt: number }) {
  const [preview, setPreview] = useState("");

  useEffect(() => {
    if (type !== "note") return;
    let alive = true;
    void getData()
      .notes.readMarkdown(id)
      .then((md) => {
        if (!alive) return;
        setPreview(
          md
            .replace(/^#+\s+/gm, "")
            .replace(/[*_`>~[\]()#=-]/g, "")
            .trim()
            .slice(0, 220),
        );
      });
    return () => {
      alive = false;
    };
  }, [id, type, updatedAt]);

  return (
    <button type="button" className={s.card} onClick={() => openItem(id)}>
      <span className={s.cardHeader}>
        {type === "folder" ? <Folder size={14} /> : <FileText size={14} />}
        <span>{title || "Untitled"}</span>
      </span>
      {type === "note" && preview ? <span className={s.cardPreview}>{preview}</span> : null}
      <span className={s.cardMeta}>{new Date(updatedAt).toLocaleDateString()}</span>
    </button>
  );
}
